import { Component, ElementRef, OnInit } from '@angular/core';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { DummyDataService } from '../../services/dummy-data/dummy-data.service';
import {
  ContactTypes,
  DateFormats,
  ModalID,
  ModalType,
  Statements,
} from '../../models/constants';
import { SharedService } from '../../services/shared.service';
import { formatPhoneNumber, notBelowZero } from '../../functions';
// Third Party
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { DateTimePickerConfig } from 'src/app/shared/models/constants';
import { format } from 'date-fns';
// models
import { InventoryAlert, AutoPurchase, PurchaseOptimizerItem } from '../../models/general';
import { Vendor } from '../../models/accounting';
import { AccountingService } from 'src/app/accounting/services/accounting.service';
import { InventoryRequestService } from 'src/app/vendor-data/inventory-request-lists/inventory-request.service';

@Component({
  selector: 'app-inventory-options',
  templateUrl: './inventory-options.component.html',
  styleUrls: ['./inventory-options.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, TooltipModule, BsDatepickerModule],
})
export class InventoryOptionsComponent implements OnInit {
  inventory_options_form: FormGroup;
  types: string[] = ['Product', 'Service'];
  item_name: string;
  item_description: string;
  item_upc: string;

  modal_type: string;
  request_type: string;
  alert_selected_row: InventoryAlert = new InventoryAlert();
  auto_purchase_selected_row: AutoPurchase = new AutoPurchase();
  optimizer_selected_row: PurchaseOptimizerItem = new PurchaseOptimizerItem();
  inventoryAlert: InventoryAlert = new InventoryAlert();
  autoPurchase: AutoPurchase = new AutoPurchase();
  purchaseOptimizerItem: PurchaseOptimizerItem = new PurchaseOptimizerItem();
  prefill_item_data: { upc?: string; name?: string; description?: string } = null;

  bsConfig: Partial<BsDatepickerConfig>;
  filteredVendors: any[] = [];
  vendorMapping = {
    rsr_group_available_qty: 'RSR Group',
    lipseys_available_qty: "Lipsey's",
    davidsons_available_qty: "davidson's",
    chattanooga_available_qty: 'Chattanooga Shooting Supply',
    zanders_available_qty: 'zanders',
    allen_arms_available_qty: 'Allen Arms Tactical'
  };
  public formatPhoneNumber = formatPhoneNumber;

  constructor(
    protected element: ElementRef,
    private fb: FormBuilder,
    public dummyDataService: DummyDataService,
    public sharedService: SharedService,
    public accountingService: AccountingService,
    public inventoryRequestService: InventoryRequestService,
  ) {
    this.bsConfig = Object.assign({}, DateTimePickerConfig);
    // Initializing Form
    this.inventory_options_form = this.fb.group({
      item_name: new FormControl({ value: null, disabled: true }, [Validators.required]),
      item_description: new FormControl({ value: null, disabled: true }, [Validators.required]),
      item_upc: new FormControl({ value: null, disabled: false }, [Validators.required]),
      requestor: new FormControl({ value: null, disabled: false }, [Validators.required]),
      alert_selected_vendors: new FormControl({ value: [], disabled: false }),
      alert_desired_qty: new FormControl({ value: 1, disabled: false }, [
        Validators.min(1),
      ]),
      alert_text_number: new FormControl(
        {
          value: this.sharedService.company_info?.phone_number?.replace(/\D/g, '') || null,
          disabled: false,
        },
        [Validators.minLength(10), Validators.maxLength(10)],
      ),
      alert_email: new FormControl(
        {
          value: this.sharedService.company_info?.company_email || null,
          disabled: false,
        },
        [Validators.email],
      ),

      auto_purchase_vendors: new FormControl({ value: [], disabled: false }),
      auto_purchase_qty: new FormControl({ value: 1, disabled: false }, [
        Validators.min(1),
      ]),
      auto_purchase_date_range: new FormControl({
        value: null,
        disabled: false,
      }),
      auto_purchase_partial_orders: new FormControl({
        value: null,
        disabled: false,
      }),
    });
  }

  ngOnInit(): void {
    if (!this.alert_selected_row) {
      this.alert_selected_row = new InventoryAlert();
    }
    if (!this.auto_purchase_selected_row) {
      this.auto_purchase_selected_row = new AutoPurchase();
    }
    if (!this.optimizer_selected_row) {
      this.optimizer_selected_row = new PurchaseOptimizerItem();
    }

    // Subscribe to item selected from the Inventory Search modal
    this.sharedService.inventory_search_item_selected$.subscribe({
      next: (item) => {
        if (item) {
          this.inventory_options_form.patchValue({
            item_upc: item.upc,
            item_name: item.mfg_part_number || item.item_number || item.manufacturer,
            item_description: item.desc1 || item.desc2,
          });
          this.item_upc = item.upc;
          this.item_name = item.mfg_part_number || item.item_number || item.manufacturer;
          this.item_description = item.desc1 || item.desc2;
          this.filterVendors(item);
        }
      }
    });

    // Initialize filteredVendors as empty for new requests
    // For edit mode, we'll populate it with all vendors so current selections are visible
    if (this.modal_type === ModalType.edit) {
      this.filteredVendors = this.accountingService.vendors;
    } else {
      this.filteredVendors = [];
    }

    if (this.modal_type === ModalType.edit) {
      if (
        this.auto_purchase_selected_row.auto_purchase_selected_vendors.length >
        0
      ) {
        this.inventory_options_form.patchValue({
          auto_purchase_vendors:
            this.auto_purchase_selected_row.auto_purchase_selected_vendors,
        });
        this.inventory_options_form.patchValue({
          auto_purchase_qty:
            this.auto_purchase_selected_row.auto_purchase_desired_qty,
        });
        const newDateRange = [
          format(
            new Date(
              this.auto_purchase_selected_row.auto_purchase_starting_date,
            ),
            DateFormats.M_D_Y_H_M_S,
          ),
          format(
            new Date(this.auto_purchase_selected_row.auto_purchase_ending_date),
            DateFormats.M_D_Y_H_M_S,
          ),
        ];

        this.inventory_options_form.patchValue({
          auto_purchase_date_range: newDateRange,
        });
        this.inventory_options_form.patchValue({
          auto_purchase_partial_orders:
            this.auto_purchase_selected_row
              .auto_purchase_partial_orders_allowed,
          requestor: this.auto_purchase_selected_row.requestor,
        });
        this.item_name =
          this.auto_purchase_selected_row.auto_purchase_item_name;
        this.item_description =
          this.auto_purchase_selected_row.auto_purchase_item_description;
        this.item_upc = this.auto_purchase_selected_row.auto_purchase_item_upc;
      } else if (this.request_type === 'optimizer') {
        this.alertSetRequiredFields();      if (this.modal_type === ModalType.edit && this.optimizer_selected_row) {
        this.inventory_options_form.patchValue({
          alert_desired_qty: this.optimizer_selected_row.desired_qty,
          requestor: this.optimizer_selected_row.requestor,
        });
        this.item_name = this.optimizer_selected_row.item_name;
        this.item_description = this.optimizer_selected_row.item_description;
        this.item_upc = this.optimizer_selected_row.item_upc;
      }      } else if (this.alert_selected_row.alert_selected_vendors.length > 0) {
        this.inventory_options_form.patchValue({
          alert_selected_vendors:
            this.alert_selected_row.alert_selected_vendors,
        });
        this.inventory_options_form.patchValue({
          alert_desired_qty: this.alert_selected_row.alert_desired_qty,
        });
        this.inventory_options_form.patchValue({
          alert_text_number: this.alert_selected_row.alert_text_number,
        });
        this.inventory_options_form.patchValue({
          alert_email: this.alert_selected_row.alert_email,
          requestor: this.alert_selected_row.requestor,
        });
        this.item_name = this.alert_selected_row.alert_item_name;
        this.item_description = this.alert_selected_row.alert_item_description;
        this.item_upc = this.alert_selected_row.alert_item_upc;
      }
    }

    // Pre-fill from vendor dashboard context menu
    if (this.prefill_item_data) {
      this.inventory_options_form.patchValue({
        item_upc: this.prefill_item_data.upc || '',
        item_name: this.prefill_item_data.name || '',
        item_description: this.prefill_item_data.description || '',
      });
      this.item_upc = this.prefill_item_data.upc || '';
      this.item_name = this.prefill_item_data.name || '';
      this.item_description = this.prefill_item_data.description || '';

      // Auto-populate vendors from live inventory without opening search modal
      if (this.prefill_item_data.upc) {
        this.sharedService.getVendorData(this.prefill_item_data.upc).subscribe({
          next: (live_inventory) => {
            if (live_inventory) {
              this.filterVendors(live_inventory);
            }
          },
          error: (err) => console.error('Error fetching vendor data for pre-fill', err),
        });
      }
    }
  }

  submitClick() {
    const isAlert = this.request_type !== 'auto_purchase';
    const formValues = this.inventory_options_form.getRawValue();

    if (isAlert) {
      this.purchaseOptimizerItem.desired_qty = formValues.alert_desired_qty;
      this.purchaseOptimizerItem.item_name = formValues.item_name || this.item_name;
      this.purchaseOptimizerItem.item_description = formValues.item_description || this.item_description;
      this.purchaseOptimizerItem.item_upc = formValues.item_upc || this.item_upc;
      this.purchaseOptimizerItem.requestor = formValues.requestor;

      this.inventoryAlert.alert_desired_qty = formValues.alert_desired_qty;
      this.inventoryAlert.alert_text_number = formValues.alert_text_number;
      this.inventoryAlert.alert_email = formValues.alert_email;
      this.inventoryAlert.alert_item_name = formValues.item_name || this.item_name;
      this.inventoryAlert.alert_item_description = formValues.item_description || this.item_description;
      this.inventoryAlert.alert_item_upc = formValues.item_upc || this.item_upc;
      let processedAlertVendors = (formValues.alert_selected_vendors || [])
        .map((v: any) => v?.contact?.display_name || v?.contact?.company_name || v['contact.display_name'] || v)
        .filter((v: any) => !!v);
      this.inventoryAlert.alert_selected_vendors = processedAlertVendors;
      this.inventoryAlert.requestor = formValues.requestor;
    } else {
      this.autoPurchase.auto_purchase_desired_qty = formValues.auto_purchase_qty;
      if (formValues.auto_purchase_date_range) {
        this.autoPurchase.auto_purchase_starting_date = formValues.auto_purchase_date_range[0];
        this.autoPurchase.auto_purchase_ending_date = formValues.auto_purchase_date_range[1];
      }
      this.autoPurchase.auto_purchase_partial_orders_allowed = formValues.auto_purchase_partial_orders;
      this.autoPurchase.auto_purchase_item_name = formValues.item_name || this.item_name;
      this.autoPurchase.auto_purchase_item_description = formValues.item_description || this.item_description;
      this.autoPurchase.auto_purchase_item_upc = formValues.item_upc || this.item_upc;
      let processedAutoVendors = (formValues.auto_purchase_vendors || [])
        .map((v: any) => v?.contact?.display_name || v?.contact?.company_name || v['contact.display_name'] || v)
        .filter((v: any) => !!v);
      this.autoPurchase.auto_purchase_selected_vendors = processedAutoVendors;
      this.autoPurchase.requestor = formValues.requestor;
    }

    if (this.modal_type === ModalType.edit) {
      if (!isAlert) {
        if ((this.auto_purchase_selected_row as any)._id) {
          this.inventoryRequestService.updateAutoPurchase((this.auto_purchase_selected_row as any)._id, this.autoPurchase).subscribe({
            next: () => {
              this.sharedService.raiseSuccessToast('Auto Purchase updated successfully!');
              this.inventoryRequestService.refreshRequests$.next();
            },
            error: (err) => this.sharedService.raiseErrorToast('Failed to update Auto Purchase')
          });
        }
      } else {
        if (this.request_type === 'optimizer') {
          this.inventoryRequestService.updatePurchaseOptimizerItem((this.optimizer_selected_row as any)._id, this.purchaseOptimizerItem).subscribe({
            next: () => {
              this.sharedService.raiseSuccessToast('Purchase Optimizer Item updated successfully!');
              this.inventoryRequestService.refreshRequests$.next();
            },
            error: (err) => this.sharedService.raiseErrorToast('Failed to update Purchase Optimizer Item')
          });
        } else if ((this.alert_selected_row as any)._id) {
          this.inventoryRequestService.updateInventoryAlert((this.alert_selected_row as any)._id, this.inventoryAlert).subscribe({
            next: () => {
              this.sharedService.raiseSuccessToast('Inventory Alert updated successfully!');
              this.inventoryRequestService.refreshRequests$.next();
            },
            error: (err) => this.sharedService.raiseErrorToast('Failed to update Inventory Alert')
          });
        }
      }
    } else {
      if (
        this.request_type === 'auto_purchase' ||
        this.autoPurchase.auto_purchase_selected_vendors.length > 0
      ) {
        this.autoPurchase.auto_purchase_item_name = this.inventory_options_form.get('item_name').value;
        this.autoPurchase.auto_purchase_item_description = this.inventory_options_form.get('item_description').value;
        this.autoPurchase.auto_purchase_item_upc = this.inventory_options_form.get('item_upc').value;
        this.autoPurchase.auto_purchase_selected_vendors = this.inventory_options_form.get('auto_purchase_vendors').value;

        (this.autoPurchase as any).company_id = this.sharedService.company_info?.company_id || this.dummyDataService.account_information?.company?.company_id;
        this.inventoryRequestService.createAutoPurchase(this.autoPurchase).subscribe({
            next: () => {
              this.sharedService.raiseSuccessToast('Auto Purchase created successfully!');
              this.inventoryRequestService.refreshRequests$.next();
            },
            error: (err) => this.sharedService.raiseErrorToast('Failed to create Auto Purchase')
        });
      }
      if (this.request_type === 'optimizer') {
        (this.purchaseOptimizerItem as any).company_id = this.sharedService.company_info?.company_id || this.dummyDataService.account_information?.company?.company_id;
        this.inventoryRequestService.createPurchaseOptimizerItem(this.purchaseOptimizerItem).subscribe({
          next: () => {
            this.sharedService.raiseSuccessToast('Purchase Optimizer Item saved successfully!');
            this.inventoryRequestService.refreshRequests$.next();
          },
          error: (err) => this.sharedService.raiseErrorToast('Failed to create Purchase Optimizer Item')
        });
      } else if (this.inventoryAlert.alert_selected_vendors?.length > 0) {
        (this.inventoryAlert as any).company_id = this.sharedService.company_info?.company_id || this.dummyDataService.account_information?.company?.company_id;
        this.inventoryRequestService.createInventoryAlert(this.inventoryAlert).subscribe({
            next: () => {
              this.sharedService.raiseSuccessToast('Inventory Alert created successfully!');
              this.inventoryRequestService.refreshRequests$.next();
            },
            error: (err) => this.sharedService.raiseErrorToast('Failed to create Inventory Alert')
        });
      }
    }
    this.sharedService.closeModal(ModalID.inventory_options);
  }

  closeModalClick(): void {
    this.inventory_options_form.reset();
    this.sharedService.closeModal(ModalID.inventory_options);
  }

  checkForNegative(event: any) {
    let control = event.target.id.toString();
    if (control === 'alert_desired_qty') {
      if (
        notBelowZero(
          this.inventory_options_form.get('alert_desired_qty').value,
        ) === 0
      ) {
        this.inventory_options_form.patchValue({ alert_desired_qty: 1 });
        this.sharedService.raiseWarningToast(Statements.greater_then_zero);
      } else {
        this.inventory_options_form.patchValue({
          alert_desired_qty: notBelowZero(
            this.inventory_options_form.get('alert_desired_qty').value,
          ),
        });
      }
    } else if (control === 'auto_purchase_qty') {
      if (
        notBelowZero(
          this.inventory_options_form.get('auto_purchase_qty').value,
        ) === 0
      ) {
        this.inventory_options_form.patchValue({ auto_purchase_qty: 1 });
        this.sharedService.raiseWarningToast(Statements.greater_then_zero);
      } else {
        this.inventory_options_form.patchValue({
          auto_purchase_qty: notBelowZero(
            this.inventory_options_form.get('auto_purchase_qty').value,
          ),
        });
      }
    }
  }

  autoPurchaseSetRequiredFields() {
    if (this.inventory_options_form.get('auto_purchase_vendors').value !== 0) {
      this.inventory_options_form
        .get('auto_purchase_qty')
        .setValidators([Validators.required, Validators.min(1)]);
      this.inventory_options_form
        .get('auto_purchase_date_range')
        .setValidators([Validators.required]);
      this.inventory_options_form
        .get('auto_purchase_partial_orders')
        .setValidators([Validators.required]);
    } else {
      this.inventory_options_form
        .get('auto_purchase_qty')
        .setValidators([Validators.min(1)]);
      this.inventory_options_form
        .get('auto_purchase_date_range')
        .setValidators([]);
      this.inventory_options_form
        .get('auto_purchase_partial_orders')
        .setValidators([]);
    }
  }

  alertSetRequiredFields() {
    if (this.request_type === 'optimizer') {
      this.inventory_options_form.get('alert_desired_qty').setValidators([Validators.required, Validators.min(1)]);
      this.inventory_options_form.get('alert_text_number').setValidators([]);
      this.inventory_options_form.get('alert_email').setValidators([]);
      return;
    }

    if (this.inventory_options_form.get('alert_selected_vendors').value !== 0) {
      this.inventory_options_form
        .get('alert_desired_qty')
        .setValidators([Validators.required, Validators.min(1)]);
      this.inventory_options_form
        .get('alert_text_number')
        .setValidators([
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
        ]);
      this.inventory_options_form
        .get('alert_email')
        .setValidators([Validators.required]);
    } else {
      this.inventory_options_form
        .get('alert_desired_qty')
        .setValidators([Validators.min(1)]);
      this.inventory_options_form
        .get('alert_text_number')
        .setValidators([Validators.minLength(10), Validators.maxLength(10)]);
      this.inventory_options_form.get('alert_email').setValidators([]);
    }
  }

  addNewCustomer() {
    this.sharedService.openAddEditContactModal(
      ModalType.add,
      ContactTypes.customer,
    );
  }

  editCustomer() {
    const requestorName = this.inventory_options_form.get('requestor').value;
    const customer = this.accountingService.customers.find(
      (c) => c.contact?.display_name === requestorName
    );
    if (customer) {
      this.sharedService.openAddEditContactModal(
        ModalType.edit,
        ContactTypes.customer,
        customer,
      );
    }
  }

  searchItem() {
    const upc = this.inventory_options_form.get('item_upc').value;
    const description = this.inventory_options_form.get('item_description').value;

    if (upc && upc.length >= 12 && !description) {
      this.sharedService.openStatusSpinner('Searching Live Inventory...');
      this.sharedService.getVendorData(upc).subscribe({
        next: (live_inventory) => {
          this.sharedService.closeStatusSpinner();
          if (live_inventory && (live_inventory.desc1 || live_inventory.mfg_part_number)) {
            // Found item, patch the form
            this.inventory_options_form.patchValue({
              item_name: live_inventory.mfg_part_number || '',
              item_description: live_inventory.desc1 || live_inventory.desc2 || '',
            });

            // Trigger vendor filtering/auto-selection
            this.filterVendors(live_inventory);
          } else {
            // Not found in live inventory, fallback to search modal
            this.sharedService.openInventorySearchModal();
          }
        },
        error: (err) => {
          console.error('Error searching live inventory', err);
          this.sharedService.closeStatusSpinner();
          this.sharedService.openInventorySearchModal();
        }
      });
    } else {
      this.sharedService.openInventorySearchModal();
    }
  }

  filterVendors(item: any) {
    if (!item) {
      this.filteredVendors = [];
      return;
    }

    const availableVendorNames: string[] = [];
    Object.keys(this.vendorMapping).forEach((key) => {
      const qty = item[key];
      if (qty !== null && qty !== undefined && qty !== '') {
        availableVendorNames.push((this.vendorMapping as any)[key]);
      }
    });

    if (availableVendorNames.length > 0) {
      const lowerAvailable = availableVendorNames.map((name) =>
        name.toLowerCase(),
      );

      // Filter and Deduplicate vendors by display name
      const uniqueVendorsMap = new Map();
      this.accountingService.vendors.forEach((v) => {
        const vName =
          (typeof v === 'string'
            ? v
            : v.contact?.display_name || v.contact?.company_name) || '';
        const lowerName = vName.toLowerCase();

        if (
          lowerAvailable.includes(lowerName) &&
          !uniqueVendorsMap.has(lowerName)
        ) {
          // Normalize to object so bindLabel works
          if (typeof v === 'string') {
            const normalizedVendor = new Vendor();
            normalizedVendor.contact.display_name = v;
            uniqueVendorsMap.set(lowerName, normalizedVendor);
          } else {
            uniqueVendorsMap.set(lowerName, v);
          }
        }
      });

      this.filteredVendors = Array.from(uniqueVendorsMap.values());

      // Automatically select all values for both dropdowns
      const allSelectedNames = this.filteredVendors
        .map((v) =>
          typeof v === 'string'
            ? v
            : v.contact?.display_name || v.contact?.company_name,
        )
        .filter((name) => !!name);

      // Use setTimeout to ensure the dropdown has items before patching
      setTimeout(() => {
        this.inventory_options_form.patchValue({
          alert_selected_vendors: allSelectedNames,
          auto_purchase_vendors: allSelectedNames,
        });
        this.alertSetRequiredFields();
        this.autoPurchaseSetRequiredFields();
      });
    } else {
      this.filteredVendors = [];
    }
  }
}
